import { useCallback, useEffect, useMemo, useState } from "react";

import { favoritesService } from "../services/favoritesService";
import type { DataTypeVerbete } from "./sinalario";
import { useSinalario } from "./sinalario";
import { useAuth } from "./auth";
import { useGamification } from "./useGamification";

/** Mesma lógica do app nativo: dia do ano % quantidade de verbetes */
export function getWordOfTheDay(
  date: Date,
  words: DataTypeVerbete[]
): DataTypeVerbete | null {
  const valid = words.filter((w) => w?.palavra?.trim());
  if (valid.length === 0) return null;
  const start = new Date(date.getFullYear(), 0, 0).getTime();
  const dayOfYear = Math.floor(
    (date.getTime() - start) / (1000 * 60 * 60 * 24)
  );
  const index = dayOfYear % valid.length;
  return valid[index];
}

export function useWordOfTheDay() {
  const { getVerbete } = useSinalario();
  const { user } = useAuth();
  const { addPoints } = useGamification();
  const [verbetes, setVerbetes] = useState<DataTypeVerbete[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  const currentWord = useMemo(
    () => getWordOfTheDay(new Date(), verbetes),
    [verbetes]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await getVerbete();
        if (!cancelled) setVerbetes(list);
      } catch {
        if (!cancelled) setVerbetes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getVerbete]);

  useEffect(() => {
    if (!currentWord || !user?.uid) {
      setIsFavorite(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const favs = await favoritesService.getFavorites(user.uid);
      const key = currentWord.uid || currentWord.palavra;
      if (!cancelled) {
        setIsFavorite(favs.some((f) => (f.uid || f.palavra) === key));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWord, user?.uid]);

  const toggleFavorite = useCallback(async () => {
    if (!currentWord || !user?.uid) return;
    const added = await favoritesService.toggleFavorite(user.uid, currentWord);
    setIsFavorite(added);
    if (added) {
      await addPoints("FAVORITE");
    }
  }, [currentWord, user?.uid, addPoints]);

  return {
    currentWord,
    loading,
    isFavorite,
    toggleFavorite,
  };
}
